import React, { memo } from "react";
import { useApp } from "@/contexts/AppContext";

/**
 * Speedometer gauge — 4-color arc + needle + big readable number
 * Memoized to prevent layout thrashing on mass re-renders.
 */
function RiskDialGauge({ item, type = "category" }) {
  const { setSelectedDetail } = useApp();
  const rawRisk = item.avg_risk || 0;
  // Backend ALWAYS sends avg_risk on 0-10 scale, so always normalize to 0-1
  const risk = Math.max(0, Math.min(1, rawRisk / 10));
  const pct = Math.round(risk * 100);
  const label = item.category || item.name || item.venture || "";

  // Geometry — half circle
  const cx = 70, cy = 70, r = 54;
  const polar = (d) => {
    const a = (d * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const arc = (f, t) => {
    const [x1, y1] = polar(f);
    const [x2, y2] = polar(t);
    return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
  };
  const bands = [
    { f: 180, t: 252, c: "var(--sev-low)" },
    { f: 252, t: 288, c: "var(--sev-medium)" },
    { f: 288, t: 333, c: "var(--sev-high)" },
    { f: 333, t: 360, c: "var(--sev-critical)" },
  ];
  const needleAng = 180 + risk * 180;
  const needleRad = (needleAng * Math.PI) / 180;
  const nx = cx + (r - 6) * Math.cos(needleRad);
  const ny = cy + (r - 6) * Math.sin(needleRad);

  const band = risk >= 0.85 ? "CRITICAL" : risk >= 0.60 ? "HIGH" : risk >= 0.40 ? "MEDIUM" : "LOW";
  const bandColor = risk >= 0.85 ? "var(--sev-critical)" : risk >= 0.60 ? "var(--sev-high)" : risk >= 0.40 ? "var(--sev-medium)" : "var(--sev-low)";

  return (
    <button
      type="button"
      data-testid={`risk-dial-${label.replace(/\s+/g, "-")}`}
      onClick={() => React.startTransition(() => setSelectedDetail({ type: type === "venture" ? "venture" : "category", payload: type === "venture" ? { name: label, signals: item.signals, critical: item.critical, avg_risk: item.avg_risk } : item }))}
      className="w-full text-left p-3 hover:bg-black/[0.04] transition-all group block border border-white/10 hover:border-white/30 rounded-sm shadow-sm"
    >
      <div className="flex items-start justify-between mb-1">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-display leading-tight truncate text-[var(--text-primary)] font-semibold">{label}</div>
        </div>
        <span className="label-micro px-1.5 py-0.5 border" style={{ color: bandColor, borderColor: bandColor }}>
          {band}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 py-2">
        <svg viewBox="0 0 140 82" className="w-[130px] h-[75px] shrink-0">
          <path d={arc(180, 360)} stroke="rgba(0,0,0,0.06)" strokeWidth="12" fill="none" />
          {bands.map((b, i) => (
            <path key={i} d={arc(b.f, b.t)} stroke={b.c} strokeWidth="11" fill="none" />
          ))}
          <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="var(--text-primary)" strokeWidth="3" strokeLinecap="round" />
          <circle cx={cx} cy={cy} r="4.5" fill="var(--text-primary)" />
          <circle cx={cx} cy={cy} r="2" fill="var(--bg-surface)" />
        </svg>

        <div className="flex flex-col items-end justify-center min-w-0 flex-1">
          <div className="font-display text-[32px] leading-none font-bold whitespace-nowrap" style={{ color: bandColor }}>{pct}</div>
          <div className="label-micro mt-1 text-primary/40 uppercase tracking-widest whitespace-nowrap">Risk Index</div>
        </div>
      </div>

      <div className="flex items-center gap-4 mt-1 border-t border-white/5 pt-2 text-[11px] text-[var(--text-secondary)]">
        <span className="font-medium flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--sev-critical)]" />
          {item.critical || 0} Critical
        </span>
        <span className="opacity-60">/ {item.signals || 0} signals</span>
        <span className="ml-auto text-[var(--text-muted)] group-hover:text-[var(--text-primary)] font-medium transition-colors">Drill Analysis →</span>
      </div>
    </button>
  );
}

export default memo(RiskDialGauge);
